import { FC, Fragment, ReactNode } from "react";
import cn from "classnames";
import CellWithDialog from "./CellWithDialog";
import styles from "./InteractiveTable.module.css";
import { COL_COLOR_MAPPING } from "./InteractiveTable.constants";
import { InteractiveTableCellProps } from "../InteractiveTableCell";
import { InteractiveTableCategory } from "./InteractiveTable.types";

export type InteractiveCellProps = Pick<
  InteractiveTableCellProps,
  "header" | "description" | "selection"
> & {
  horizontalCategory: { categoryGroup: string };
  verticalCategory: { categoryGroup: string };
};

export type InteractiveTableProps = {
  colTitle: string;
  rowTitle: string;
  colHeader: {
    hasCards?: boolean;
    content?: string;
    header: InteractiveTableCategory;
  }[];
  rowHeader: InteractiveTableCategory[];
  rowCellsChildren: ReactNode[][];
  clearModelSlot: ReactNode;
};

const InteractiveTable: FC<InteractiveTableProps> = ({
  colTitle,
  rowTitle,
  colHeader,
  rowHeader,
  rowCellsChildren,
  clearModelSlot,
}) => {
  return (
    <div
      className="hidden pt-8 md:pt-10 lg:block lg:pt-14"
      data-table="itsm-table"
      id="itsm-table"
    >
      <div className="border-swdc-gray-100 from-swdc-cool-gray-200 grid grid-cols-[9.625rem_repeat(5,minmax(0,1fr))] border bg-linear-to-l to-white text-center">
        <div className="bg-swdc-gray-400 text-subhead-xs row-span-3 px-4 pt-2 pb-3 text-white italic">
          {rowTitle}
        </div>
        <div className="bg-swdc-black text-subhead-xs col-span-5 py-2 text-white italic">
          {colTitle}
        </div>
        <div
          className="bg-swdc-black col-span-5 flex px-4 text-white"
          aria-hidden
        >
          <div className="relative mx-auto h-4 w-full max-w-[60.472rem]">
            <div
              className={cn(
                styles.arrow,
                "from-swdc-silver-200 via-swdc-silver-300 via-swdc-silver-400 to-swdc-orange-400 absolute size-full bg-linear-to-r",
              )}
            ></div>
          </div>
        </div>
        {colHeader?.map((col, idx) => (
          <div
            data-table="col-header"
            key={`interactive-table-col-${col.header.categoryKey}-${idx}`}
            className={cn(
              "bg-swdc-black flex justify-center pt-2 pb-4",
              COL_COLOR_MAPPING[idx]?.text,
            )}
          >
            <CellWithDialog
              title={col.header.value}
              dialogContent={col.header.content}
              analytics={col.header.analytics}
            />
          </div>
        ))}
        {rowHeader?.map((row, idx) => (
          <Fragment key={`interactive-table-row-${row.categoryKey}-${idx}`}>
            <div className="bg-swdc-gray-400 text-swdc-silver-200 px-4">
              <div
                className={cn(
                  "border-swdc-silver-200 flex w-full justify-end",
                  idx === 0 ? "border-t-2" : "border-t",
                  idx + 1 === rowHeader.length ? "pt-8 pb-10" : "py-8",
                )}
              >
                <CellWithDialog
                  title={row.value}
                  dialogContent={row.content}
                  analytics={row.analytics}
                />
              </div>
            </div>
            {rowCellsChildren?.[idx]}
          </Fragment>
        ))}
      </div>
      {clearModelSlot}
    </div>
  );
};

export default InteractiveTable;
